import { HttpResponse } from "../../models/HttpResponse";
import axios, { Method, AxiosResponse, AxiosError } from 'axios';

class Http {
  static token: string | null;
  private normalizeResponseData = (payload: unknown): unknown => {
    if (typeof payload === "string") {
      const trimmedPayload = payload.trim();
      const looksLikeJson =
        (trimmedPayload.startsWith("{") && trimmedPayload.endsWith("}")) ||
        (trimmedPayload.startsWith("[") && trimmedPayload.endsWith("]"));

      if (!looksLikeJson) {
        return payload;
      }

      try {
        return JSON.parse(trimmedPayload);
      } catch {
        return payload;
      }
    }

    return payload;
  };

  public init(token: string) {
    Http.token = token;
  }

  private getHeaders = () => {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      Accept: "application/json",
    };

    if (Http.token) {
      headers.Authorization = `Bearer ${Http.token}`;
    }

    return headers;
  };

  public request = async <T>(
    method: Method,
    url: string,
    data?: any,
    headers?: Record<string, string>
  ): Promise<HttpResponse<T>> => {
    try {
      const response = await axios({
        method,
        url,
        data,
        headers: { ...this.getHeaders(), ...headers },
      });

      if (response.status >= 200 && response.status < 300) {
         return { data: this.normalizeResponseData(response.data) as T };
      }

      return this.handleErrorResponse(response);
    } catch (error: any) {
      return this.handleAxiosError(error);
    }
  };

  private handleErrorResponse = (response: AxiosResponse): HttpResponse<any> => {
    if (process.env.NODE_ENV != "production") console.error("API Error:", response)
    return {
      error: response.statusText,
      errorMessage: response.data?.message || undefined,
    };
  };

  private handleAxiosError = (error: AxiosError): HttpResponse<any> => {
    if (process.env.NODE_ENV != "production") console.error("API Axios Error:", error)
    if (axios.isAxiosError(error) && error.response) {
      if (error.response.status === 401) {
        return {
          error: 'Unauthorized',
          errorMessage: 'You are not authorized to access this resource.',
        };
      }
      return {
        error: `Status:${error.response.statusText} Message:${error.message}`,
        errorMessage: JSON.stringify(error)
      };
    }

    return {
      error: 'Error',
      errorMessage: 'An error occurred while processing your request.',
    };
  };
}

const HttpService = new Http();

export { HttpService };
